import { StringContext } from "../../../core/StringContext";
import { StringDevModule } from "../core/StringDevModule";
import { StringObject } from "../../../objects/StringObject";
import { GridAdapter } from "./adapters/GridAdapter";
import { GridManager } from "./GridManager";
import { GridOverlay } from "./GridOverlay";
import { GridHUD } from "./GridHUD";
import { GridSerializer } from "./GridSerializer";

import { ColumnsAdapter } from "./adapters/ColumnsAdapter";
import { RowsAdapter } from "./adapters/RowsAdapter";
import { CenterAdapter } from "./adapters/CenterAdapter";
import { RuleOfThirdsAdapter } from "./adapters/RuleOfThirdsAdapter";
import { PhiGridAdapter } from "./adapters/PhiGridAdapter";
import { GoldenRectangleAdapter } from "./adapters/GoldenRectangleAdapter";
import { HarmonicArmatureAdapter } from "./adapters/HarmonicArmatureAdapter";
import { DotGridAdapter } from "./adapters/DotGridAdapter";
import { PentagramAdapter } from "./adapters/PentagramAdapter";
import { RabatmentAdapter } from "./adapters/RabatmentAdapter";
import { resolveDevtoolsCollisionOffset } from "../core/StringDevtoolsOverlayLayout";
import { StringDevViewportLayer } from "../core/StringDevViewportLayer";
import { buildGridCSS } from "./StringGrid.css";

/**
 * Resolves a stable ID for an element, using its existing `id` attribute
 * or the StringObject's id as fallback.
 */
function resolveElementId(element: HTMLElement, object: StringObject): string {
  return element.id || object.id;
}

function resolveGridDepth(element: HTMLElement): number {
  let depth = 0;
  let current = element.parentElement;

  while (current) {
    if (current.hasAttribute("data-string-grid-root")) {
      depth += 1;
    }
    current = current.parentElement;
  }

  return depth;
}

interface GridTriggerEntry {
  object: StringObject;
  hud: GridHUD;
  depth: number;
}

interface GridTriggerMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
  stackOffsetY: number;
  elementDocX?: number;
  elementDocY?: number;
  width?: number;
  height?: number;
}

/**
 * StringDevLayout — developer utility module for layout overlays.
 *
 * Usage:
 *   <div string="grid">...</div>
 *
 * No configuration attributes needed. All configuration happens
 * through the interactive HUD panel (hover top-right corner).
 *
 * Supports multiple grid types via the adapter pattern:
 * - Columns, Rows, Center
 * - Rule of Thirds, Phi Grid, Golden Rectangle, Harmonic Armature
 * - Extensible via `st.use(StringDevLayout, { adapters: [MyAdapter] })`
 */
export class StringDevLayout extends StringDevModule {
  private gridManager: GridManager = new GridManager();
  private viewportLayer: StringDevViewportLayer;
  private overlays: Map<string, GridOverlay> = new Map();
  private huds: Map<string, GridHUD> = new Map();
  private elementMap: Map<string, HTMLElement> = new Map();
  private triggerEntries: Map<string, GridTriggerEntry> = new Map();
  private triggerMeasurements: Map<string, GridTriggerMeasurement> = new Map();
  private enabled: boolean = true;
  private needsMeasure = false;
  private hasPendingMutate = false;
  static override devtool = {
    id: "layout",
    label: "Layout",
    icon: "grid",
    order: 20,
    styles: buildGridCSS,
  };

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "grid";
    this.attributesToMap = [];
    this.viewportLayer = this.acquireViewportLayer("devtools-chrome", 10011);
  }

  // ── Lifecycle ─────────────────────────────────────────

  onInit(): void {
    this.registerBuiltInAdapters();
    this.registerExternalAdapters();
    this.ensureTriggerLayer();
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    const elementId = resolveElementId(element, object);
    element.setAttribute("data-string-grid-root", "");
    const gridDepth = resolveGridDepth(element);
    element.setAttribute("data-string-grid-depth", String(gridDepth));
    element.style.setProperty("--string-grid-stack-offset", "0px");
    this.elementMap.set(elementId, element);

    this.gridManager.load(elementId);

    const world = this.viewportLayer.getWorldElement(this.getWorldHost());
    const overlay = new GridOverlay(element, world);
    this.overlays.set(elementId, overlay);

    const hud = new GridHUD(
      element,
      this.gridManager.getAdapterRegistry(),
      {
        onAdd: (type) => this.handleAdd(elementId, type),
        onRemove: (id) => this.handleRemove(elementId, id),
        onToggle: (id) => this.handleToggle(elementId, id),
        onSettingChange: (id, key, value) => this.handleSettingChange(elementId, id, key, value),
        onReorder: (draggedId, targetId, placement) =>
          this.handleReorder(elementId, draggedId, targetId, placement),
        onMoveToEnd: (id) => this.handleMoveToEnd(elementId, id),
        onSelectLayout: (layoutId) => this.handleSelectLayout(elementId, layoutId),
        onUpdateLayoutMinWidth: (layoutId, minWidth) =>
          this.handleUpdateLayoutMinWidth(elementId, layoutId, minWidth),
        onPreviewResponsiveLayout: (mediumMinWidth, largeMinWidth, activeLayoutId) =>
          this.handlePreviewResponsiveLayout(
            elementId,
            mediumMinWidth,
            largeMinWidth,
            activeLayoutId,
          ),
        onCommitResponsiveLayout: (mediumMinWidth, largeMinWidth, activeLayoutId) =>
          this.handleCommitResponsiveLayout(
            elementId,
            mediumMinWidth,
            largeMinWidth,
            activeLayoutId,
          ),
        onExport: () => this.handleExport(elementId),
        onImport: () => this.handleImport(elementId),
        onRenameInstance: (instanceId, name) =>
          this.handleRenameInstance(elementId, instanceId, name),
        onHUDOpen: () => this.handleHUDOpen(elementId),
        onHUDClose: () => this.handleHUDClose(elementId),
        onLayoutPanelOpen: () => this.handleLayoutPanelOpen(elementId),
        onLayoutPanelClose: () => this.handleLayoutPanelClose(elementId),
      },
    );
    this.huds.set(elementId, hud);
    hud.setTargetId(elementId);
    this.ensureTriggerLayer();
    this.viewportLayer.getWorldElement(this.getWorldHost()).appendChild(hud.getChromeElement());
    this.triggerEntries.set(elementId, { object, hud, depth: gridDepth });

    this.renderElement(elementId);
    this.refreshHUD(elementId);
    overlay.setEnabled(this.enabled);
    hud.setEnabled(this.enabled);
    this.scheduleTriggerSync();
  }

  onObjectDisconnected(object: StringObject): void {
    const element = object.htmlElement;
    const elementId = resolveElementId(element, object);
    this.destroyElement(elementId);
  }

  onResize(): void {
    for (const [elementId, overlay] of this.overlays) {
      overlay.syncViewBox();
      this.renderElement(elementId);
      this.refreshHUD(elementId);
    }
    this.scheduleTriggerSync();
  }

  onScroll(): void {
    if (!this.enabled) return;
    this.needsMeasure = true;
  }

  onScrollMeasure(): void {
    if (!this.enabled || !this.needsMeasure) return;
    this.collectTriggerMeasurements();
    this.needsMeasure = false;
    for (const [elementId, entry] of this.triggerEntries) {
      this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
    }
    this.hasPendingMutate = false;
  }

  onMutate(): void {
    if (!this.enabled || !this.hasPendingMutate) return;
    for (const [elementId, entry] of this.triggerEntries) {
      this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
    }
    this.hasPendingMutate = false;
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.scheduleTriggerSync();
  }

  destroy(): void {
    for (const [id] of this.overlays) {
      this.destroyElement(id);
    }
    this.gridManager.clear();
    super.destroy();
  }

  public setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) {
      return;
    }

    this.enabled = enabled;
    for (const overlay of this.overlays.values()) {
      overlay.setEnabled(enabled);
    }
    for (const hud of this.huds.values()) {
      hud.setEnabled(enabled);
    }
    this.applyTriggerLayerState();
    if (enabled) {
      for (const elementId of this.overlays.keys()) {
        this.renderElement(elementId);
      }
      this.scheduleTriggerSync();
    } else {
      this.hasPendingMutate = false;
    }
    this.emitDevtoolState();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  protected getDevtoolActiveState(): boolean {
    return this.enabled;
  }

  protected setDevtoolActiveState(active: boolean): void {
    this.setEnabled(active);
  }

  // ── HUD Callbacks ─────────────────────────────────────

  private handleAdd(elementId: string, type: string): void {
    this.gridManager.addInstance(elementId, type);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleRemove(elementId: string, instanceId: string): void {
    const instance = this.gridManager.findInstance(elementId, instanceId);
    if (instance) {
      const adapter = this.gridManager.getAdapter(instance.type);
      const overlay = this.overlays.get(elementId);
      if (adapter && overlay) {
        overlay.clearInstance(instanceId, adapter);
      }
    }
    this.gridManager.removeInstance(elementId, instanceId);
    this.refreshHUD(elementId);
  }

  private handleToggle(elementId: string, instanceId: string): void {
    this.gridManager.toggleVisibility(elementId, instanceId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleSettingChange(
    elementId: string,
    instanceId: string,
    key: string,
    value: any,
  ): void {
    this.gridManager.updateSetting(elementId, instanceId, key, value);
    this.renderElement(elementId);
  }

  private handleReorder(
    elementId: string,
    draggedId: string,
    targetId: string,
    placement: "before" | "after",
  ): void {
    this.gridManager.reorderInstance(elementId, draggedId, targetId, placement);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleMoveToEnd(elementId: string, instanceId: string): void {
    this.gridManager.moveInstanceToEnd(elementId, instanceId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleSelectLayout(elementId: string, layoutId: string): void {
    this.gridManager.selectLayout(elementId, layoutId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleUpdateLayoutMinWidth(elementId: string, layoutId: string, minWidth: number): void {
    this.gridManager.updateLayoutMinWidth(elementId, layoutId, minWidth);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handlePreviewResponsiveLayout(
    elementId: string,
    mediumMinWidth: number,
    largeMinWidth: number,
    activeLayoutId: string,
  ): void {
    const layouts = this.gridManager.getLayouts(elementId);
    const large = layouts[0];
    const medium = layouts[1];
    if (!large || !medium) {
      return;
    }

    this.gridManager.updateLayoutMinWidth(elementId, medium.id, mediumMinWidth, false);
    this.gridManager.updateLayoutMinWidth(elementId, large.id, largeMinWidth, false);
    this.gridManager.selectLayout(elementId, activeLayoutId, false);
    this.renderElement(elementId);
  }

  private handleCommitResponsiveLayout(
    elementId: string,
    mediumMinWidth: number,
    largeMinWidth: number,
    activeLayoutId: string,
  ): void {
    const layouts = this.gridManager.getLayouts(elementId);
    const large = layouts[0];
    const medium = layouts[1];
    if (!large || !medium) {
      return;
    }

    this.gridManager.updateLayoutMinWidth(elementId, medium.id, mediumMinWidth, false);
    this.gridManager.updateLayoutMinWidth(elementId, large.id, largeMinWidth, false);
    this.gridManager.selectLayout(elementId, activeLayoutId, false);
    this.gridManager.save(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleRenameInstance(elementId: string, instanceId: string, name: string): void {
    this.gridManager.renameInstance(elementId, instanceId, name);
    this.refreshHUD(elementId);
  }

  private handleHUDOpen(elementId: string): void {
    this.syncSelectedLayoutToResolved(elementId);
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleHUDClose(elementId: string): void {
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleLayoutPanelOpen(elementId: string): void {
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleLayoutPanelClose(elementId: string): void {
    this.renderElement(elementId);
    this.refreshHUD(elementId);
  }

  private handleExport(elementId: string): void {
    const layouts = this.gridManager.getLayouts(elementId);
    const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
    const data = GridSerializer.serialize(layouts, selectedLayoutId);
    const slug = elementId.replace(/[^a-z0-9]/gi, "-").toLowerCase() || "element";
    GridSerializer.toFile(`string-grid-${slug}.json`, data);
  }

  private handleImport(elementId: string): void {
    GridSerializer.fromFile()
      .then((raw) => {
        const result = GridSerializer.deserialize(raw, this.gridManager.getAdapterRegistry());
        if (!result) return;
        this.gridManager.replaceLayouts(elementId, result.layouts, result.selectedLayoutId);
        this.renderElement(elementId);
        this.refreshHUD(elementId);
      })
      .catch(() => {
        // user cancelled or invalid file — nothing to do
      });
  }

  // ── Internal ──────────────────────────────────────────

  private renderElement(elementId: string): void {
    const overlay = this.overlays.get(elementId);
    if (!overlay) return;

    const { instances } = this.resolveActiveLayoutState(elementId);
    overlay.render(instances, this.gridManager.getAdapterRegistry());
  }

  private refreshHUD(elementId: string): void {
    const hud = this.huds.get(elementId);
    if (!hud) return;

    const { instances, selectedLayoutId, activeLayoutId } =
      this.resolveActiveLayoutState(elementId);
    const layouts = this.gridManager.getLayouts(elementId);
    hud.refresh(instances, layouts, selectedLayoutId, activeLayoutId);
  }

  private resolveActiveLayoutState(elementId: string): {
    instances: ReturnType<GridManager["getSelectedInstances"]>;
    selectedLayoutId: string;
    activeLayoutId: string;
  } {
    const hud = this.huds.get(elementId);
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const resolvedLayoutId = this.gridManager.getResolvedLayoutId(elementId, viewportWidth);
    const isManualPreview = hud?.isHUDOpen() === true;

    if (!isManualPreview) {
      this.syncSelectedLayoutToResolved(elementId, resolvedLayoutId);
    }

    const selectedLayoutId = this.gridManager.getSelectedLayoutId(elementId);
    return {
      instances: this.gridManager.getSelectedInstances(elementId),
      selectedLayoutId,
      activeLayoutId: isManualPreview ? selectedLayoutId : resolvedLayoutId,
    };
  }

  private syncSelectedLayoutToResolved(elementId: string, resolvedLayoutId?: string): void {
    const targetLayoutId =
      resolvedLayoutId ??
      this.gridManager.getResolvedLayoutId(
        elementId,
        this.data.viewport.windowWidth || window.innerWidth,
      );
    this.gridManager.selectLayout(elementId, targetLayoutId, false);
  }

  private destroyElement(elementId: string): void {
    this.overlays.get(elementId)?.destroy();
    this.overlays.delete(elementId);
    this.huds.get(elementId)?.destroy();
    this.huds.delete(elementId);
    this.triggerEntries.delete(elementId);
    this.triggerMeasurements.delete(elementId);
    this.elementMap.get(elementId)?.removeAttribute("data-string-grid-root");
    this.elementMap.get(elementId)?.removeAttribute("data-string-grid-depth");
    this.elementMap.get(elementId)?.style.removeProperty("--string-grid-stack-offset");
    this.elementMap.delete(elementId);
  }

  private ensureTriggerLayer(): void {
    const layer = this.viewportLayer.ensure(this.getWorldHost());
    layer.setAttribute("data-string-grid-trigger-layer", "");
  }

  private applyTriggerLayerState(): void {
    return;
  }

  private scheduleTriggerSync(): void {
    this.ensureTriggerLayer();
    this.needsMeasure = true;
    this.collectTriggerMeasurements();
    this.needsMeasure = false;
    for (const [elementId, entry] of this.triggerEntries) {
      this.applyTriggerMeasurement(entry, this.triggerMeasurements.get(elementId));
    }
    this.hasPendingMutate = false;
  }

  private collectTriggerMeasurements(): void {
    for (const [elementId, entry] of this.triggerEntries) {
      this.triggerMeasurements.set(elementId, this.measureTrigger(entry));
    }
  }

  private measureTrigger(entry: GridTriggerEntry): GridTriggerMeasurement {
    const object = entry.object;
    const element = object.htmlElement;
    if (!this.enabled || !element || !element.isConnected) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const width =
      object.getProperty<number>("layout-width") ?? element.offsetWidth ?? element.clientWidth ?? 0;
    const measuredSize = object.getProperty<number>("size");
    const layoutHeight =
      object.getProperty<number>("layout-height") ??
      element.offsetHeight ??
      element.clientHeight ??
      0;
    const height = measuredSize != null && measuredSize > 0 ? measuredSize : layoutHeight;
    const contentX = object.getProperty<number>("layout-doc-left") ?? 0;
    const contentY = this.getObjectDocY(object);

    if (width <= 0 || height <= 0) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();
    const viewportLeft = contentX - scrollLeft;
    const viewportTop = contentY - scrollTop;
    const isOffscreen =
      viewportTop + height < 0 ||
      viewportTop > viewportHeight ||
      viewportLeft + width < 0 ||
      viewportLeft > viewportWidth;

    if (isOffscreen) {
      return { visible: false, docX: 0, docY: 0, stackOffsetY: 0 };
    }

    const triggerDocX = contentX + width - 12 - 28;
    const baseTriggerDocY = contentY + 12;
    const stackOffsetY = this.resolveTriggerStackOffset(entry, triggerDocX, baseTriggerDocY);

    return {
      visible: true,
      docX: triggerDocX,
      docY: baseTriggerDocY + stackOffsetY,
      stackOffsetY,
      elementDocX: contentX,
      elementDocY: contentY,
      width,
      height,
    };
  }

  private applyTriggerMeasurement(
    entry: GridTriggerEntry,
    measurement: GridTriggerMeasurement | undefined,
  ): void {
    if (!measurement || !measurement.visible) {
      entry.hud.setAnchorVisible(false);
      entry.object.htmlElement.style.setProperty("--string-grid-stack-offset", "0px");
      return;
    }

    entry.hud.setAnchorVisible(true);
    entry.object.htmlElement.style.setProperty(
      "--string-grid-stack-offset",
      `${measurement.stackOffsetY}px`,
    );
    entry.hud.setAnchorPosition(measurement.docX, measurement.docY);

    const overlay = this.overlays.get(resolveElementId(entry.object.htmlElement, entry.object));
    if (overlay && measurement.elementDocX != null && measurement.elementDocY != null && measurement.width != null && measurement.height != null) {
      overlay.setPosition(measurement.elementDocX, measurement.elementDocY, measurement.width, measurement.height);
    }
  }

  protected getWorldHost(): HTMLElement {
    return this.data.scroll.container ?? document.body ?? document.documentElement;
  }

  private getObjectDocY(object: StringObject): number {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");
    if (start != null && size != null && size > 0) {
      return start;
    }
    return object.getProperty<number>("layout-doc-top") ?? 0;
  }

  private getViewportScrollLeft(): number {
    const scroller = this.data.scroll.elementContainer;
    return scroller?.scrollLeft ?? 0;
  }

  private getViewportScrollTop(): number {
    const scroller = this.data.scroll.elementContainer;
    return scroller?.scrollTop ?? 0;
  }

  private resolveTriggerStackOffset(
    entry: GridTriggerEntry,
    triggerDocX: number,
    triggerDocY: number,
  ): number {
    return resolveDevtoolsCollisionOffset(
      entry.object.htmlElement,
      triggerDocX,
      triggerDocY,
      (ancestorId) => {
        const ancestorObject = this.objectManager.all.get(ancestorId);
        if (!ancestorObject) {
          return null;
        }

        const ancestorWidth =
          ancestorObject.getProperty<number>("layout-width") ??
          ancestorObject.htmlElement.offsetWidth ??
          ancestorObject.htmlElement.clientWidth ??
          0;

        return {
          docX:
            (ancestorObject.getProperty<number>("layout-doc-left") ?? 0) + ancestorWidth - 12 - 28,
          docY: this.getObjectDocY(ancestorObject) + 12,
        };
      },
    );
  }

  private registerBuiltInAdapters(): void {
    const builtIn: GridAdapter[] = [
      new ColumnsAdapter(),
      new RowsAdapter(),
      new CenterAdapter(),
      new RuleOfThirdsAdapter(),
      new PhiGridAdapter(),
      new GoldenRectangleAdapter(),
      new HarmonicArmatureAdapter(),
      new DotGridAdapter(),
      new PentagramAdapter(),
      new RabatmentAdapter(),
    ];

    for (const adapter of builtIn) {
      this.gridManager.registerAdapter(adapter);
    }
  }

  private registerExternalAdapters(): void {
    const external = this.settings["adapters"] as GridAdapter[] | undefined;
    if (!external) return;

    for (const adapter of external) {
      this.gridManager.registerAdapter(adapter);
    }
  }
}

export { StringDevLayout as StringGrid };
